/* global runtime */
// Payroll run rules shared by the client-side payroll calculation: the
// withholding mode of a run, the credit account of a custom deduction and
// holiday pay carried forward from the previous year. Each block mirrors a
// functions/payroll module and is kept identical by an equivalence test.
// Loaded as a classic script before backend.jsx and shares its binding scope.

const payrollMonthEnd = (value) => {
  const period = String(value || todayIso()).slice(0, 7);
  const [year, month] = period.split('-').map(Number);
  return `${period}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
};

// Withholding mode chosen on the payroll run. Mirrors
// functions/payroll/payrollTaxDeductionMode.ts, which documents the three modes.
const payrollTaxDeductionModes = ['standard', 'tax-card', 'fifty-percent'];
const normalizePayrollTaxDeductionMode = (value) => {
  const mode = String(value ?? '').trim();
  return payrollTaxDeductionModes.includes(mode) ? mode : 'standard';
};

const payrollEmployeeTaxRate = (employee, paymentDate, taxDeductionMode = 'standard') => {
  if (normalizePayrollTaxDeductionMode(taxDeductionMode) === 'fifty-percent') return 50;
  const rate = Number(employee.taxDeductionPercent);
  const taxCardValid = employee.taxCardStatus === 'fetched' && typeof employee.taxCardValidTo === 'string' &&
    employee.taxCardValidTo >= payrollMonthEnd(paymentDate) && employee.taxDeductionPercent != null &&
    Number.isFinite(rate) && rate >= 0 && rate <= 100 && !['table', 'tabell'].includes(String(employee.taxCardType || '').toLowerCase());
  if (!taxCardValid) return 50;
  return rate;
};

// Mirrors functions/payroll/payrollDeductionAccounts.ts:
// the accounts a user-defined deduction may be credited to (5919 stays the default).
const payrollDeductionCreditAccounts = ['5919', '2610', '2650', '2690'];
// A standard deduction always keeps its catalog account.
const payrollCustomDeductionCreditAccount = (id, kind, entry = {}, existing = {}) => {
  const catalog = payrollStandardPayTypes.find((entry) => entry.id === id);
  if (catalog?.kind === 'Trekk') return catalog.accountFromCode;
  if (kind !== 'Trekk' || catalog) return entry.accountFromCode ?? existing.accountFromCode;
  const requested = String(entry.accountFromCode ?? existing.accountFromCode ?? '').trim();
  return payrollDeductionCreditAccounts.includes(requested) ? requested : undefined;
};

// Holiday pay earned in the previous year and still unpaid, per employee on a
// run. Mirrors holidayPayCarriedForwardByEmployee in
// functions/payroll/holidayPayCarryForward.ts, which documents the rule; the
// equivalence test in functions/payroll/holidayPayCarryForward.test.ts keeps
// them identical.
const payrollRunHolidayYear = (run) => {
  const year = Number(String(run?.paymentDate || run?.period || '').trim().slice(0, 4));
  return Number.isInteger(year) && year > 0 ? year : null;
};
const payrollHolidayItemsOfType = (line, payType) => safeArray(line?.items)
  .filter((item) => item?.payType === payType)
  .reduce((sum, item) => sum + (Number.isFinite(Number(item?.amount)) ? Number(item.amount) : 0), 0);
const payrollHolidayPayCarriedForwardByEmployee = ({ runs, run } = {}) => {
  const year = payrollRunHolidayYear(run);
  if (!year) return {};
  const targetId = String(run?.id ?? '').trim();
  const balances = new Map();
  const add = (employeeId, amount) => {
    const key = String(employeeId ?? '').trim();
    if (!key || !amount) return;
    balances.set(key, (balances.get(key) || 0) + amount);
  };
  safeArray(runs).forEach((entry) => {
    if (!['approved', 'paid'].includes(String(entry?.status ?? '').trim())) return;
    if (targetId && String(entry?.id ?? '').trim() === targetId) return;
    const entryYear = payrollRunHolidayYear(entry);
    safeArray(entry?.lines).forEach((line) => {
      if (entryYear === year - 1) {
        const accrual = Number(line?.holidayPayAccrual);
        add(line?.employeeId, Math.max(0, Number.isFinite(accrual) ? accrual : 0));
        add(line?.employeeId, -payrollHolidayItemsOfType(line, 'holiday-pay-current'));
      } else if (entryYear === year) {
        add(line?.employeeId, -payrollHolidayItemsOfType(line, 'holiday-pay-previous'));
      }
    });
  });
  safeArray(run?.lines).forEach((line) => add(line?.employeeId, -payrollHolidayItemsOfType(line, 'holiday-pay-previous')));
  const result = {};
  safeArray(run?.lines).forEach((line) => {
    const key = String(line?.employeeId ?? '').trim();
    if (key) result[key] = Math.max(0, Math.round(((balances.get(key) || 0) + Number.EPSILON) * 100) / 100);
  });
  return result;
};
// Read by the "Utbetaling av feriepenger" dialog on the payroll page. Uses the
// current business state, so payouts approved after the draft was saved count.
const getPayrollHolidayPayCarriedForward = (runOrId) => {
  const state = mergeState(runtime.appState);
  const runs = safeArray(state.payrollRuns);
  const runId = typeof runOrId === 'string' ? runOrId : runOrId?.id;
  const run = runs.find((entry) => entry.id === runId) || (typeof runOrId === 'object' ? runOrId : null);
  return run ? payrollHolidayPayCarriedForwardByEmployee({ runs, run }) : {};
};
window.PlummePayrollHolidayPay = {
  carriedForwardByEmployee: payrollHolidayPayCarriedForwardByEmployee,
  carriedForwardForRun: getPayrollHolidayPayCarriedForward,
};

// Employment in a payroll period and supported tax rules. Mirrors
// functions/payroll/payrollEmploymentRules.ts, which documents the rules;
// functions/payroll/payrollEmploymentRules.test.ts runs both over the same cases.
const payrollEmploymentIsoDate = (value) => {
  const day = String(value ?? '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : '';
};
const payrollPeriodBounds = (period) => {
  const month = String(period ?? '').trim().slice(0, 7);
  const [year, monthNumber] = month.split('-').map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) return null;
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return { start: `${month}-01`, end: `${month}-${String(lastDay).padStart(2, '0')}` };
};
const payrollEmployedInPeriod = (employee = {}, period) => {
  const bounds = payrollPeriodBounds(period);
  if (!bounds) return employee?.active !== false;
  const start = payrollEmploymentIsoDate(employee?.employmentStartDate);
  const end = payrollEmploymentIsoDate(employee?.employmentEndDate);
  if (start && start > bounds.end) return false;
  if (end) return end >= bounds.start;
  return employee?.active !== false;
};
// Status shown on the employee card: an end date in the past means the
// employment has ended even if the stored `active` flag was never cleared.
const payrollEmploymentStatus = (employee = {}, today = todayIso()) => {
  const backend = employee?._backend || employee || {};
  const end = payrollEmploymentIsoDate(backend.employmentEndDate);
  const start = payrollEmploymentIsoDate(backend.employmentStartDate);
  if (employee?.active === false || backend.active === false || (end && end <= today)) return 'ended';
  if (start && start > today) return 'future';
  return 'active';
};
const payrollSupportedTaxRules = ['ordinary'];
const payrollTaxRuleSupported = (rule) => {
  const value = String(rule ?? '').trim();
  return !value || payrollSupportedTaxRules.includes(value);
};
const payrollUnsupportedTaxRuleEmployees = ({ run, employees } = {}) => {
  const byId = new Map(safeArray(employees).map((employee) => [String(employee?.id ?? '').trim(), employee]));
  const hasPay = (line) => safeArray(line?.items).some((item) => Number(item?.amount) > 0) || Number(line?.grossSalary) > 0 || Number(line?.netPay) > 0;
  return safeArray(run?.lines)
    .filter(hasPay)
    .map((line) => ({ line, employee: byId.get(String(line?.employeeId ?? '').trim()) }))
    .filter(({ employee }) => employee && !payrollTaxRuleSupported(employee.taxRule))
    .map(({ line, employee }) => ({
      employeeId: String(employee.id ?? '').trim(),
      employeeName: String(employee.name || line?.employeeName || '').trim(),
      taxRule: String(employee.taxRule ?? '').trim(),
    }));
};
const assertPayrollRunTaxRulesSupported = (state, run) => {
  const [first] = payrollUnsupportedTaxRuleEmployees({ run, employees: state?.employees });
  if (!first) return;
  const rule = t(`pay.taxRules.${first.taxRule.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase())}`, first.taxRule);
  throw new Error(t('pay.taxRuleUnsupportedError', { rule, employee: first.employeeName }));
};

// NOK amount of an employee expense line. A line in foreign currency is paid
// only with a stated NOK amount; without one it returns null and must never be
// paid as if the foreign amount were kroner. Mirrors
// functions/payroll/employeeExpenseCurrency.ts.
const payrollExpenseCurrency = (line = {}) => String(line?.currency ?? '').trim().toUpperCase() || 'NOK';
const payrollExpenseNokAmount = (line = {}) => {
  if (payrollExpenseCurrency(line) === 'NOK') return num(line.amount);
  const nok = Number(line?.amountNok);
  return Number.isFinite(nok) && nok > 0 ? Math.round(nok * 100) / 100 : null;
};
const payrollTravelMissingNokLines = (travel = {}) => safeArray(travel?.expenseLines)
  .filter((line) => normalizeTravelPaymentMethod(line.paymentMethod) === 'private' && payrollExpenseNokAmount(line) == null);

// Splits a pension premium across the chosen run lines by their OTP basis
// (pension-eligible gross, capped at the OTP ceiling). The premium replaces the
// calculated OTP of each line it lands on. The last share takes the rounding.
const allocatePayrollPensionPremium = ({ lines, employeeIds, total, payrollSettings } = {}) => {
  const amount = Math.round(num(total) * 100) / 100;
  if (!(amount > 0)) throw new Error(t('pay.pensionAllocation.amountRequired'));
  const chosen = safeArray(lines).filter((line) => safeArray(employeeIds).includes(line.employeeId));
  if (!chosen.length) throw new Error(t('pay.pensionAllocation.noEmployees'));
  if (chosen.length === 1) return [{ employeeId: chosen[0].employeeId, employeeName: chosen[0].employeeName, amount }];
  const pension = getPayrollPensionSettings(payrollSettings || {});
  const cap = pension.baseAmount > 0 && pension.upperG > 0 ? pension.baseAmount * pension.upperG / 12 : Infinity;
  const weights = chosen.map((line) => Math.max(0, Math.min(payrollPensionEligibleGross(line, payrollSettings || {}), cap)));
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (!(totalWeight > 0)) throw new Error(t('pay.pensionAllocation.noBasis'));
  let allocated = 0;
  return chosen.map((line, index) => {
    const share = index === chosen.length - 1
      ? Math.round((amount - allocated) * 100) / 100
      : Math.round(amount * weights[index] / totalWeight * 100) / 100;
    allocated += share;
    return { employeeId: line.employeeId, employeeName: line.employeeName, amount: share };
  }).filter((entry) => entry.amount > 0);
};

window.PlummePayrollEmployment = {
  employedInPeriod: payrollEmployedInPeriod,
  status: payrollEmploymentStatus,
  taxRuleSupported: payrollTaxRuleSupported,
  unsupportedTaxRuleEmployees: payrollUnsupportedTaxRuleEmployees,
  allocatePensionPremium: allocatePayrollPensionPremium,
  expenseNokAmount: payrollExpenseNokAmount,
};
